import cn from 'classnames';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { BUSINESS_REASON_ID } from '../../../constants/reasons';
import { NO_CURRENCY } from '../../../constants/shell';
import { selectIsProductMarketplaceEnabled } from '../../../store/selectors/organizations';
import { selectBusinessReasonById } from '../../../store/selectors/reasons';
import { IOneLink } from '../../../types/oneLink';
import { IReport } from '../../../types/reports';
import { formatZonedDateTime, isValidDate, resolveStoredTimezone } from '../../../utils/date';
import {
  calculateDaysLeft,
  getReportGiftBudget,
  getReportItemSources,
  getReportStatusAndDate,
  isAwaitingAddress,
} from '../../../utils/reports';
import { Card } from '../../index';
import ReportGiftBudget from '../../ReportsComponent/components/ReportGiftBudget';
import ReportItemSource from '../../ReportsComponent/components/ReportItemSource';
import SendDate from '../SendDate/SendDate';

import styles from './DetailsCard.module.scss';

interface IProps {
  className?: string;
  controls?: React.ReactNode;
  instance: IReport | IOneLink;
  isOneLink?: boolean;
}

const DetailsCard = ({ className, controls, instance, isOneLink }: IProps) => {
  const { name: reason } = useSelector(selectBusinessReasonById(instance?.[BUSINESS_REASON_ID])) || {};
  const isMarketplaceEnabled = useSelector(selectIsProductMarketplaceEnabled);

  const itemSources = React.useMemo(() => getReportItemSources(instance), [instance]);
  const giftBudget = React.useMemo(() => getReportGiftBudget(instance), [instance]);

  const { date, dateLabel, createdAtDate, delayedShippingExpirationDate } = React.useMemo(
    () =>
      isOneLink
        ? { date: undefined, dateLabel: undefined, createdAtDate: undefined, delayedShippingExpirationDate: undefined }
        : getReportStatusAndDate(instance as IReport),
    [instance, isOneLink],
  );
  const { internal_notes: notes } = React.useMemo(() => instance, [instance]);
  const { recipients_count } = React.useMemo(() => instance as IOneLink, [instance]);
  const { use_times_count, max_submit_count } = React.useMemo(() => instance as IOneLink, [instance]);

  const daysLeftMessage = React.useMemo(() => {
    if (!isOneLink) {
      return calculateDaysLeft((instance as IReport)?.delayed_shipping_expiration);
    }
    return undefined;
  }, [instance, isOneLink]);

  const isAwaitingStatus = React.useMemo(() => isAwaitingAddress(instance as IReport), [instance]);

  const isExpirationDateValid = React.useMemo(
    () => delayedShippingExpirationDate && isValidDate(new Date(delayedShippingExpirationDate)),
    [delayedShippingExpirationDate],
  );

  const zonedExpirationDate = React.useMemo(() => {
    if ('delayed_shipping_expiration' in instance && isValidDate(new Date(instance?.delayed_shipping_expiration))) {
      return formatZonedDateTime(
        instance.delayed_shipping_expiration,
        resolveStoredTimezone(instance.timezone_metadata, 'delayed_shipping_expiration'),
      );
    }
  }, [instance]);

  return (
    <Card
      className={cn(styles.container, className)}
      topClassName={styles.head}
      top={
        <React.Fragment>
          <div className={styles.dates}>
            {createdAtDate && <SendDate label="Created date:" value={createdAtDate} />}
            {date && dateLabel && <SendDate label={dateLabel} value={date} />}
          </div>
        </React.Fragment>
      }
      middleClassName={styles.detailInfo}
      middle={
        <React.Fragment>
          <div className={styles.option}>Confirmed recipients:</div>
          <div className={cn(styles.value, styles.recipients)} title="Confirmed recipients">
            <span>{use_times_count || 0}</span>
            {max_submit_count && (
              <>
                &nbsp;
                <span title="Redemption limit">{`/ ${max_submit_count}`}</span>
              </>
            )}
          </div>

          {isMarketplaceEnabled && (
            <>
              <div className={styles.option}>Item source:</div>
              <div className={cn(styles.value, styles.notes)}>
                <ReportItemSource sources={itemSources} />
              </div>

              <div className={styles.option}>Gift budget:</div>
              <div className={cn(styles.value, styles.notes)}>
                <ReportGiftBudget budget={giftBudget} />
              </div>
            </>
          )}

          <div className={styles.option}>Business reason:</div>
          <div className={cn(styles.value, styles.reason)}>{reason ? reason : NO_CURRENCY.label}</div>

          <div className={styles.option}>Internal notes:</div>
          <div className={cn(styles.value, styles.notes)}>{notes ? notes : NO_CURRENCY.label}</div>
          {isAwaitingStatus && isExpirationDateValid && (
            <React.Fragment>
              <div className={styles.option}>Expiration date:</div>
              <div className={cn(styles.value, styles.expDate)}>
                {zonedExpirationDate}
                <span className={styles.daysLeft}> ({daysLeftMessage})</span>
              </div>
            </React.Fragment>
          )}
          {recipients_count ? (
            <>
              <div className={styles.option}>2FA recipients:</div>
              <div className={cn(styles.value, styles.notes)}>{recipients_count.toLocaleString()}</div>
            </>
          ) : null}
        </React.Fragment>
      }
      bottomClassName={styles.controls}
      bottom={controls}
    />
  );
};

export default DetailsCard;
